import { useState, FC, useEffect, useRef } from "react";
import { Select } from "antd";

import CustomOption from "./CustomOption";
import CreateCustomAccountability from './CreateCustomAccountability';

import { Wrapper } from "./styled";
import { IAccountability } from "Redux/ListAccountability/types";

interface IProps {
  text: string;
  options: any;
  error: string;
  value: any;
  isWidth?: boolean;
  isMobile?: boolean;
  disabled?: boolean;
  onSelect: (value: any) => void;
  onCreate: (value: string, callback: (success: any) => void) => void;
}

export const getGroupedOptions = (options: IAccountability[] = [], customOptions: IAccountability[] = [], onDelete?: (value: string) => void) => {
  const handleDelete = (value: string) => {
    if (onDelete) {
      onDelete(value);
    }
  }

  const optionsFormatted = options
    .map(i => ({
      value: i.id,
      label: i.name
    }));

  const customOptionsFormatted = customOptions
    .map(i => ({
      value: i.id,
      label: <CustomOption value={i.id} label={i.name} onDelete={handleDelete} />,
      originalLabel: i.name
    }));

  return [
    {
      label: "",
      options: [...optionsFormatted],
    },
    {
      label: customOptions.length > 0 ? "Custom Accountabilities" : "",
      options: [...customOptionsFormatted],
    },
  ];
};

export const getOptionWithLabel = (value: string, options: IAccountability[] = [], customOptions: IAccountability[] = []) => {
  const allOptions = [...options, ...customOptions];
  const option = allOptions.find(i => i.id === value) || allOptions.find(i => i.name === value);

  if (!option && value) {
    return {
      value,
      label: value
    };
  }

  if (!option) {
    return undefined;
  }

  return {
    value: option?.id,
    label: option?.name
  };
}

const AccountabilitySelect: FC<IProps> = ({ text, options, error, disabled, onSelect, onCreate, value, isWidth, isMobile, ...rest }) => {
  const [searchValue, setSearchValue] = useState("");
  const [dropdownHeight, setDropdownHeight] = useState(200);

  const selectRef = useRef<any>(null);
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerHeight < 700) {
        setDropdownHeight(100);
      } else if (window.innerHeight < 800) {
        setDropdownHeight(150);
      } else {
        setDropdownHeight(200);
      }
    };
    
    // Set initial height
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  }

  const handleCreate = (value: string, callback: (success: any) => void) => {
    onCreate(value, callback);
    setSearchValue("");
  }

  const handleSelect = (value: any) => {
    onSelect(value);
    setSearchValue("");

    // Blur the search input field
    if (selectRef.current) {
      selectRef.current.blur();
    }
  }

  return (
    <Wrapper $error={!!error}>
      <Select
        {...rest}
        ref={selectRef}
        getPopupContainer={(trigger) => trigger.parentNode.parentNode}
        value={value}
        showSearch
        searchValue={searchValue}
        onSelect={handleSelect}
        style={{
          width: "100%"
        }}
        optionFilterProp="value"
        placeholder="Select"
        autoClearSearchValue={false}
        filterOption={(input, option) =>
          ((typeof option?.label === 'string' ? option.label : option?.originalLabel).toLowerCase() ?? "").includes(input.toLowerCase())
        }
        filterSort={(optionA, optionB) => {
          const labelA = typeof optionA?.label === 'string' ? optionA.label.toLowerCase() : "";
          const labelB = typeof optionB?.label === 'string' ? optionB.label.toLowerCase() : "";

          return labelA.localeCompare(labelB);
        }}
        labelInValue
        listHeight={dropdownHeight}
        dropdownRender={(menu) => {
          return (
            <>
              {menu}
              <CreateCustomAccountability
                onCreate={handleCreate}
                onSelect={handleSelect}
              />
            </>
          )
        }}
        options={options}
        disabled={disabled}
        onSearch={handleSearch}
        placement={isMobile ? "bottomLeft" : "bottomRight"}
      />
    </Wrapper>
  );
};

export default AccountabilitySelect;
